import React, { useEffect } from 'react';
import { Platform, TouchableOpacity, View } from 'react-native';
import styled from 'styled-components/native';

import { ButtonContainerWithMinMax } from 'components/Button/components';
import { LoadingOverlay } from 'components/LoadingOverlay/LoadingOverlay';
import { useSafeParams } from 'components/Routes/useSafeParams';
import {
  ErrorContainer,
  ValidationText,
} from 'components/Screens/LoginScreen/styled';
import { NHSWebViewModal } from 'components/Screens/NHSWebViewModal';
import { LOGIN_CALLBACK } from 'config/deepLinking';
import { ButtonTypes } from 'config/index';
import { useHistory } from 'cross-platform/react-router';
import { roles } from 'cross-platform/utils/roleProps';
import { useLoginWithOIDC } from 'lib/api/hooks/useLogin/useLoginWithOIDC';

import { SvgNHSButton } from './SvgNHSButton';

export const NHSPaddingContainer = styled.View`
  flex-direction: column;
  margin-bottom: 32px;
  display: flex;
  align-items: stretch;
  padding: 0 8px 0;
`;
export const ErrorText = styled(ValidationText)`
  text-align: center;
`;

const errorMessage =
  'Unable to log in. Please try again or contact us for support.';

export const NHSLoginButton = ({
  flagHide = false,
  onWebViewVisibilityChange,
}: {
  flagHide?: boolean;
  onWebViewVisibilityChange?: (visible: boolean) => void;
}) => {
  const {
    login,
    loading,
    error,
    androidAuthUrl,
    clearAndroidAuthUrl,
  } = useLoginWithOIDC();
  const { productReference } = useSafeParams();
  const history = useHistory();

  // Lets the parent screen hide its back/close buttons while the Android WebView is open —
  // its elevation can't paint over buttons living outside this component's own parent tree.
  useEffect(() => {
    onWebViewVisibilityChange?.(Platform.OS === 'android' && !!androidAuthUrl);
  }, [androidAuthUrl, onWebViewVisibilityChange]);

  // The aspect ration for the NHS svg button. Used to preserve correct sizing.
  // The numbers used taken from the viewbox of the svg.
  const svgAspectRatio = 320.22 / 59;

  const handleSubmit = async (): Promise<void> => {
    const loginType = 'nhs';
    login(loginType);
  };

  // TODO: Hook up a runtime feature flag
  if (flagHide) {
    return null;
  }

  return (
    <>
      {Platform.OS === 'android' && androidAuthUrl ? (
        <NHSWebViewModal
          visible={true}
          url={androidAuthUrl}
          callbackUrl={LOGIN_CALLBACK}
          onSuccess={path => {
            clearAndroidAuthUrl();
            history.replace(`/${productReference}/${path}`, { fromNHS: true });
          }}
          onCancel={clearAndroidAuthUrl}
          onError={clearAndroidAuthUrl}
        />
      ) : null}
      <NHSPaddingContainer>
        <ButtonContainerWithMinMax type={ButtonTypes.PRIMARY_BUTTON}>
          <TouchableOpacity
            {...roles('nhs-login-button')}
            onPress={handleSubmit}
            disabled={loading}
            accessible={true}
            accessibilityLabel="Continue to NHS login"
            accessibilityRole="button"
          >
            <View
              style={{
                aspectRatio: svgAspectRatio,
              }}
            >
              <SvgNHSButton colorTheme="normal" />
            </View>
          </TouchableOpacity>
        </ButtonContainerWithMinMax>
        {error ? (
          <ErrorContainer {...roles('nhs-login-error')}>
            <ErrorText>{errorMessage}</ErrorText>
          </ErrorContainer>
        ) : null}
      </NHSPaddingContainer>
      {loading ? <LoadingOverlay /> : null}
    </>
  );
};

export default NHSLoginButton;
